// BUILD-046 correction cycle 1bb88b3e (stage B1, item N1): the Compact / Comfortable density preference, kept across tab changes.
//
// One preference for the whole My Work page: switching between Mine, Shared and Team keeps it, and so does a reload (localStorage key 'build046.density').
// It is a display preference of the pilot, not tied to the Caller (a Caller change does not reset it). The first read falls back to the key the page used
// before ('mywork-density'), so the choice a person already made is not lost. A storage that is missing or refuses to write (a private window) leaves the
// preference in memory for the life of the page: the choice still holds across tabs, it just does not survive a reload.
//
// The module-level `density` store is what the views read (useSyncExternalStore(density.subscribe, density.get)); `createDensityStore` takes the storage so
// the tests use a fake one. Erasable TypeScript only; no imports.

export type Density = 'comfortable' | 'compact';

export const DENSITY_STORAGE_KEY = 'build046.density';
/** The key MyWorkPage used before the toolbar was shared by the three views. Read once as a fallback, never written. */
export const LEGACY_DENSITY_KEY = 'mywork-density';

export const DENSITY_OPTIONS: readonly { value: Density; label: string }[] = Object.freeze([
  { value: 'comfortable' as Density, label: 'Comfortable' },
  { value: 'compact' as Density, label: 'Compact' },
]);

export interface StorageLike {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
}

export function isDensity(value: unknown): value is Density {
  return value === 'comfortable' || value === 'compact';
}

export interface DensityStore {
  get: () => Density;
  set: (next: Density) => void;
  /** Switches to the other density; returns the new one. */
  toggle: () => Density;
  subscribe: (listener: () => void) => () => void;
}

function safeGet(storage: StorageLike | null, key: string): string | null {
  if (storage === null) return null;
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

export function createDensityStore(storage: StorageLike | null): DensityStore {
  const stored = safeGet(storage, DENSITY_STORAGE_KEY);
  const legacy = safeGet(storage, LEGACY_DENSITY_KEY);
  let value: Density = isDensity(stored) ? stored : isDensity(legacy) ? legacy : 'comfortable';
  const listeners = new Set<() => void>();
  function set(next: Density): void {
    if (!isDensity(next) || next === value) return;
    value = next;
    if (storage !== null) {
      try {
        storage.setItem(DENSITY_STORAGE_KEY, next);
      } catch {
        // the choice still holds for this page
      }
    }
    for (const listener of [...listeners]) listener();
  }
  return {
    get: () => value,
    set,
    toggle() {
      set(value === 'compact' ? 'comfortable' : 'compact');
      return value;
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

function browserStorage(): StorageLike | null {
  try {
    const candidate = (globalThis as { localStorage?: StorageLike }).localStorage;
    return candidate ?? null;
  } catch {
    return null;
  }
}

/** The page's density preference. */
export const density: DensityStore = createDensityStore(browserStorage());
